import type { OpenUrlWithKind } from '@api/generated/models';
import { Auth } from '@feature/auth';
import { useUser } from '@services/auth/use_user';
import { LocationSelectorService } from '@services/location_selector.service';
import { useService } from '@visiba/instantiation';
import { ReactNode, SyntheticEvent } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

interface Props {
	__TEMP__on_OPW_WebContentButton_proxy_invoke?: (flexibleIdentifierData: {
		url: string;
		openUrlWith: string;
		requiresAuthentication: boolean;
	}) => void;
	url: string;
	requiresAuthentication: boolean;
	openWith: OpenUrlWithKind;
	sourceButtonId?: number;
	children?: ReactNode;
	className?: string;
}

export function Routeable({
	requiresAuthentication,
	url,
	children,
	openWith,
	sourceButtonId,
	__TEMP__on_OPW_WebContentButton_proxy_invoke,
	className,
}: Props): JSX.Element {
	const locationSelectorService = useService(LocationSelectorService);
	const urlWithButtonId = addButtonIdToCareNavigationUrl(url, sourceButtonId);
	const parsedUrl =
		openWith === 'IFrame' ? convertUrlToEmbeddedWebLink(locationSelectorService.location.route, urlWithButtonId) : urlWithButtonId;
	const user = useUser();

	// A not-yet-authenticated patient is forced to sign in before we navigate when the button is
	// configured to require authentication (the office "Kräv autentisering" / RequiresAuthentication
	// setting). For booking buttons this is enabled per-button in office the same way.
	// Only internal in-app navigation is gated here. External/new-tab/iframe links keep their
	// existing behaviour, and host-driven navigation (proxy invoke / __TEMP__overrideNavigation)
	// handles authentication itself, so those are skipped.
	const rendersAsInternalLink = openWith !== 'IFrame' && isInternalUrl(url);
	const hasNavigationOverride = __TEMP__on_OPW_WebContentButton_proxy_invoke != null || window.__TEMP__overrideNavigation != null;
	const shouldForceLogin = user == null && requiresAuthentication && rendersAsInternalLink && !hasNavigationOverride;

	if (shouldForceLogin) {
		return (
			<Auth.SignInSheet
				returnUrl={urlWithButtonId}
				trigger={
					<StyledTriggerButton type='button' className={className}>
						{children}
					</StyledTriggerButton>
				}
			/>
		);
	}

	function handleIntercept(event: SyntheticEvent<HTMLAnchorElement>): void {
		if (__TEMP__on_OPW_WebContentButton_proxy_invoke) {
			event.preventDefault();

			__TEMP__on_OPW_WebContentButton_proxy_invoke?.({
				url: openWith === 'IFrame' ? encodeURIComponent(urlWithButtonId) : urlWithButtonId,
				openUrlWith: openWith,
				requiresAuthentication: requiresAuthentication,
			});

			return;
		}

		if (window.__TEMP__overrideNavigation) {
			event.preventDefault();
			window.__TEMP__overrideNavigation(parsedUrl);

			return;
		}

		if (requiresAuthentication && user == null) {
			// External / new-tab links that require authentication are blocked here. Internal links
			// are handled by the force-login branch above, so this only guards the non-internal cases.
			event.preventDefault();

			return;
		}
	}

	if (openWith === 'NewTab' && url.includes('http')) {
		return (
			<a href={urlWithButtonId} onClick={handleIntercept} target='_blank' rel='noreferrer' className={className}>
				{children}
			</a>
		);
	}

	if (openWith === 'SameTab' && url.includes('http')) {
		return (
			<a href={parsedUrl} onClick={handleIntercept} className={className}>
				{children}
			</a>
		);
	}

	if (openWith === 'IFrame') {
		if (window.__TEMP__overrideNavigation || __TEMP__on_OPW_WebContentButton_proxy_invoke) {
			return (
				<a href={parsedUrl} onClick={handleIntercept} className={className}>
					{children}
				</a>
			);
		}

		return (
			<Link to={parsedUrl} onClick={handleIntercept} className={className}>
				{children}
			</Link>
		);
	}

	if (window.__TEMP__overrideNavigation || __TEMP__on_OPW_WebContentButton_proxy_invoke) {
		return (
			<a href={urlWithButtonId} onClick={handleIntercept} className={className}>
				{children}
			</a>
		);
	}

	return (
		<Link to={urlWithButtonId} onClick={handleIntercept} className={className}>
			{children}
		</Link>
	);
}

function isInternalUrl(url: string): boolean {
	// Same-origin = internal in-app navigation. Resolving against the current origin makes
	// relative URLs internal while scheme-absolute (https://other) and protocol-relative
	// (//other) URLs resolve to a different origin and are treated as external.
	try {
		return new URL(url, window.location.origin).origin === window.location.origin;
	} catch {
		return false;
	}
}

const StyledTriggerButton = styled.button`
	all: unset;
	cursor: pointer;

	&:focus-visible {
		outline: revert;
	}
`;

function addButtonIdToCareNavigationUrl(url: string, buttonId: number | undefined): string {
	if (buttonId == null) {
		return url;
	}

	const isAbsoluteUrl = /^[a-z][a-z\d+\-.]*:/i.test(url);
	const parsedUrl = new URL(url, window.location.origin);
	const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);
	if (!pathSegments.includes('navigation')) {
		return url;
	}

	parsedUrl.searchParams.set('buttonId', `${buttonId}`);

	return isAbsoluteUrl ? parsedUrl.toString() : `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
}

function convertUrlToEmbeddedWebLink(prefix: string, url: string): string {
	return `${prefix}/ew?url=${encodeURIComponent(url)}`;
}
